import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Companies from '../../service/Companies';
import { Button, Skeleton, Stack, TextField } from '@mui/material';
import AttToken from '../../helpers/attToken';
import queryClient from '../../service/query';
import { trackEventMatomo } from '../../helpers/matomo';
import { Form } from '../../StyledComponents/Input/generic';
import SaveIcon from '@mui/icons-material/Save';
import QueryFn from '../../helpers/QueryFn';

const companySrvc = new Companies();
const queryFn = new QueryFn();

type regraType = {
  digit: number,
  lowercase: number,
  min: number,
  special: number,
  uppercase: number,
  expiration: number,
  noRepeatLastPassword: number
};

export default function Password() {
  const { t } = useTranslation('translation');
  const [regra, setRegra] = useState<regraType>({} as regraType);

  const { data, isLoading } = queryFn.useFetch('GET_REGRAS',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const response = await companySrvc.getAllPoliticaSenha(body);
          return response;
        }
      } catch (err) {
        console.error(err);
      }
    },
  );

  useEffect(() => {
    if (data) {
      setRegra({ ...data });
    }
  }, [isLoading]);

  const handleChange = (e: any, key: keyof typeof regra) => {
    setRegra({ ...regra, [key]: parseInt(e.target.value) });
  };

  const { mutate, isPending: loadingChange } = queryFn.useMutate('CHANGE_REGRA',
    async () => {
      toast.promise(async () => {
        try {
          const token = await AttToken();
          if (token) {
            const body = { ...token, empresa: token.userEmpresa, update: regra };
            await companySrvc.editaPoliticaSenha(body);
          }
          return queryClient.resetQueries({ queryKey: ['GET_REGRAS'] });
        } catch (err) {
          return err;
        }
      }, {
        pending: t('settingsPage.changingPasswordPolicies'),
        success: t('settingsPage.passwordPoliciesChangedSuccessfully'),
        error: t('settingsPage.errorChangingPasswordPolicies'),
      });
    });

  return (
    <Form>
      <h1 style={{ marginBottom: '10px' }}>{t('settingsPage.password.modify')}</h1>
      <Stack
        width={'100%'}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        {isLoading || loadingChange
          ? <>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              spacing={2}
            >
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
            </Stack>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton variant='rounded' width={'100%'} height={40} />
              <Skeleton width={100} height={60} />
            </Stack>
          </>
          : <>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              spacing={2}
            >
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minNmbr')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'digit')}
                value={regra.digit}
              />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minUpper')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'uppercase')}
                value={regra.uppercase} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minLower')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'lowercase')}
                value={regra.lowercase} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minSpecial')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'special')}
                value={regra.special} />
            </Stack>
            <Stack
              width={'100%'}
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.minChar')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'min')}
                value={regra.min} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.expiration')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                onChange={(e) => handleChange(e, 'expiration')}
                type='number'
                value={regra.expiration} />
              <TextField
                sx={{ width: '100%' }}
                size='small'
                label={t('settingsPage.password.prevent')}
                onClick={() => trackEventMatomo('gestão de senha', 'click', 'input', 'edita valor')}
                type='number'
                onChange={(e) => handleChange(e, 'noRepeatLastPassword')}
                value={regra.noRepeatLastPassword} />
              <Button
                endIcon={<SaveIcon />}
                disabled={loadingChange}
                className='button is-success'
                type="button"
                onClick={() => {
                  trackEventMatomo('gestão de senha', 'click', 'button', 'salva');
                  mutate('');
                }}
              >
                {t('save')}
              </Button>
            </Stack>
          </>
        }
      </Stack>
    </Form>
  );
}
